import React, { useState } from 'react';
import { useAppSelector } from 'hooks';

import { Button } from '~components';
import ClickableIcon from '~components/ClickableIcon';
import { faPlus } from '~components/icon';
import Link from '~components/Link';
import Select from '~components/Select';
import { UNASSIGNED_WORKER_LABEL } from '~constants/common';

import { selectUsers } from '~care-workers/selectors/users';
import { selectClients } from '~main/selectors/clients/clients';

type ParticipantType = 'users' | 'clients';

interface QuickAddItem {
  full_name: string;
  user_id?: number;
  client_id?: number;
  isNew: true;
}

interface ReferenceParticipant {
  id: string | number;
  full_name?: string;
}

interface SummaryParticipant {
  id?: string | number | null;
  full_name?: string;
  user_id?: number;
  client_id?: number;
  service_type?: { name?: string };
  start_time?: string;
  end_time?: string;
}

type SummaryAppointment = {
  id?: string | number | null;
  date?: string;
  start_time?: string;
  end_time?: string;
  notes?: string | null;
  service_type?: { id?: number; name?: string };
  location?: { name?: string };
  location_override_id?: number | string;
  client?: { full_name?: string };
  users?: SummaryParticipant[];
  clients?: SummaryParticipant[];
  user?: { id?: number; full_name?: string } | null;
};

interface ComponentProps {
  appointment: SummaryAppointment;
  onEditSettings: () => void;
  onQuickAdd: (type: ParticipantType, item: QuickAddItem) => void;
}

const buildOptions = (fullList: ReferenceParticipant[], assignedIds: Set<number>) =>
  fullList
    .filter(({ id }) => !assignedIds.has(Number(id)))
    .map(({ id, full_name }) => ({ label: full_name ?? '', value: Number(id) }));

const GroupAppointmentSummary: React.FC<ComponentProps> = ({ appointment, onEditSettings, onQuickAdd }) => {
  const allUsers = useAppSelector(selectUsers) as ReferenceParticipant[];
  const allClients = useAppSelector(selectClients) as ReferenceParticipant[];

  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const groupName = appointment?.client?.full_name ?? '';

  const groupWorkerName = appointment?.user?.full_name;

  const users: SummaryParticipant[] = appointment?.users ?? [];
  const clients: SummaryParticipant[] = appointment?.clients ?? [];

  const assignedUserIds = new Set(users.map((u) => Number(u.user_id)).filter((n) => !Number.isNaN(n)));
  const assignedClientIds = new Set(clients.map((c) => Number(c.client_id)).filter((n) => !Number.isNaN(n)));

  const userOptions = buildOptions(allUsers, assignedUserIds);
  const clientOptions = buildOptions(allClients, assignedClientIds);

  const handleAdd = (type: ParticipantType) => {
    const selectedId = type === 'users' ? selectedUserId : selectedClientId;
    const options = type === 'users' ? userOptions : clientOptions;
    if (!selectedId) return;
    const selected = options.find((opt) => opt.value === selectedId);
    if (!selected) return;

    onQuickAdd(type, {
      full_name: selected.label,
      [type === 'users' ? 'user_id' : 'client_id']: selected.value,
      isNew: true,
    });

    if (type === 'users') setSelectedUserId(null);
    else setSelectedClientId(null);
  };

  const renderReadOnlyField = (label: string, value: string | undefined | null) => (
    <div className="d-flex mb-2">
      <span className="fw-bold me-2 group-appointment-summary__label">{label}:</span>
      <span>{value ?? '—'}</span>
    </div>
  );

  return (
    <div className="p-2 group-appointment-summary" data-cy="group-appointment-summary">
      {/* Group name */}
      <div className="mb-3">
        <h5 className="fw-bold" data-cy="group-summary-name">
          {groupName}
        </h5>
      </div>

      {/* Session details */}
      <div className="mb-4">
        <h6 className="fw-bold mb-2">Session Details</h6>
        {renderReadOnlyField('Date', appointment?.date)}
        {renderReadOnlyField('Start Time', appointment?.start_time)}
        {renderReadOnlyField('End Time', appointment?.end_time)}
        {renderReadOnlyField('Service Type', appointment?.service_type?.name)}
        {renderReadOnlyField('Group Worker', groupWorkerName)}
        {renderReadOnlyField(
          'Location',
          appointment?.location?.name ?? appointment?.location_override_id?.toString(),
        )}
        {renderReadOnlyField('Notes', appointment?.notes)}
      </div>

      {/* Quick add — workers */}
      <label
        className="mb-3 group-appointment-summary__quick-add"
        data-cy="group-summary-quick-add-worker"
      >
        <span className="fw-bold me-2">Add care worker:</span>
        <Select
          className="select"
          id="group-summary-user-select"
          name="group-summary-user-select"
          options={userOptions}
          value={selectedUserId}
          onChange={(_name, value) => setSelectedUserId(value || null)}
          placeholder="Select care worker..."
          isClearable
        />
        <ClickableIcon
          className="confirm-add-icon"
          color={null}
          id="group-summary-user-add-icon"
          icon={faPlus}
          onClick={() => handleAdd('users')}
          disabled={!selectedUserId}
          toolTip="Add Care Worker"
          ariaLabel="Add Care Worker"
          dataCy="group-summary-user-add-icon"
        />
      </label>

      {/* Quick add — clients */}
      <label
        className="mb-4 group-appointment-summary__quick-add"
        data-cy="group-summary-quick-add-client"
      >
        <span className="fw-bold me-2">Add client:</span>
        <Select
          className="select"
          id="group-summary-client-select"
          name="group-summary-client-select"
          options={clientOptions}
          value={selectedClientId}
          onChange={(_name, value) => setSelectedClientId(value || null)}
          placeholder="Select client..."
          isClearable
        />
        <ClickableIcon
          className="confirm-add-icon"
          color={null}
          id="group-summary-client-add-icon"
          icon={faPlus}
          onClick={() => handleAdd('clients')}
          disabled={!selectedClientId}
          toolTip="Add Client"
          ariaLabel="Add Client"
          dataCy="group-summary-client-add-icon"
        />
      </label>

      {/* Workers list */}
      <div className="mb-4">
        <h6 className="fw-bold mb-2">Care Workers</h6>
        {users.length > 0 ? (
          <ul className="list-unstyled">
            {users.map((item) => (
              <li key={item.id ?? item.user_id} className="mb-1">
                <Link to={`/appointments/${item.id}`} data-cy={`group-summary-user-view-link-${item.id}`}>
                  {item.full_name ?? (item.user_id != null ? `Worker #${item.user_id}` : UNASSIGNED_WORKER_LABEL)}
                </Link>
                {item.service_type?.name && (
                  <span className="ms-2 text-muted small">
                    — {item.service_type.name}
                    {item.start_time && `, ${item.start_time}`}
                    {item.end_time && ` - ${item.end_time}`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No care workers assigned.</p>
        )}
      </div>

      {/* Clients list */}
      <div className="mb-4">
        <h6 className="fw-bold mb-2">Clients</h6>
        {clients.length > 0 ? (
          <ul className="list-unstyled">
            {clients.map((item) => (
              <li key={item.id ?? item.client_id} className="mb-1">
                <Link to={`/appointments/${item.id}`} data-cy={`group-summary-client-view-link-${item.id}`}>
                  {item.full_name ?? `Client #${item.client_id}`}
                </Link>
                {item.service_type?.name && (
                  <span className="ms-2 text-muted small">
                    — {item.service_type.name}
                    {item.start_time && `, ${item.start_time}`}
                    {item.end_time && ` - ${item.end_time}`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted">No clients assigned.</p>
        )}
      </div>

      {/* Edit Settings button */}
      <div className="mt-3">
        <Button
          color="secondary"
          size="sm"
          onClick={onEditSettings}
          dataCy="group-summary-edit-settings-button"
        >
          Edit Settings
        </Button>
      </div>
    </div>
  );
};

export default GroupAppointmentSummary;
